/**
 * The server's entry path pattern and template, cached for a day, and the
 * client-side port of the two substitutions that use them.
 *
 * Both functions mirror the server pipeline in `journal_config_service` —
 * `_sanitize_journal_title`, then `_resolve_journal_path` and
 * `render_journal_template` from `journal.py` — and are proven byte-identical
 * by the shared fixture table in `tests/fixtures/journal_defaults_cases.json`.
 * They exist so the write phase can build a new entry with no connection.
 */

import { apiGet } from '@/shared/services/api';
import { registerReadPolicy } from '@shared/offline';

export const JOURNAL_DEFAULTS_URL = '/api/journal/entries/defaults';

// Two constants that change about never: a day's cache costs nothing and means
// a new entry can still be built the first time the connection is gone.
registerReadPolicy(JOURNAL_DEFAULTS_URL, { strategy: 'cache-first', ttlMs: 24 * 60 * 60 * 1000 });

export interface JournalDefaults {
  path_pattern: string;
  template: string;
  title_max_length: number;
}

/** Fetch the defaults; served from the cache for a day once seen. */
export async function getJournalDefaults(): Promise<JournalDefaults> {
  return apiGet<JournalDefaults>(JOURNAL_DEFAULTS_URL);
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

interface CalendarDate {
  y: number;
  m: number;
  d: number;
  dow: number;
  monthName: string;
}

/** A calendar date parsed as local, never shifted by a timezone. */
function parseDate(iso: string): CalendarDate {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const utc = new Date(Date.UTC(y, m - 1, d));
  // Python's %A is Monday-first; JS getUTCDay is Sunday-first.
  const dow = (utc.getUTCDay() + 6) % 7;
  return { y, m, d, dow, monthName: MONTH_NAMES[m - 1] ?? '' };
}

const two = (n: number): string => String(n).padStart(2, '0');

// Python's `\s` (and `str.isspace`, which drives `.strip()`) is not JS's: it
// includes U+001C-U+001F and U+0085 and excludes the BOM, where JS is exactly
// the reverse. Collapsing with JS `\s` would eat a BOM the server keeps, so
// both sides use this one faithful class. Shared with journalContent, which
// strips bodies and collapses excerpt whitespace by the same rule.
export const PY_SPACE =
  '\\u0009-\\u000d\\u001c-\\u0020\\u0085\\u00a0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000';
const PY_SPACE_RUN = new RegExp(`[${PY_SPACE}]+`, 'g');
const PY_SPACE_EDGES = new RegExp(`^[${PY_SPACE}]+|[${PY_SPACE}]+$`, 'g');
const PY_SPACE_TRAIL = new RegExp(`[${PY_SPACE}]+$`);
const PY_SPACE_BEFORE_MD = new RegExp(`[${PY_SPACE}]+\\.md$`);

/** Mirror of `_sanitize_journal_title`: filename-safe, single-spaced, bounded. */
function sanitizeTitle(title: string, max: number): string {
  let cleaned = title
    .replace(/[\\/:"*?<>|]/g, '-')
    .replace(PY_SPACE_RUN, ' ')
    .replace(PY_SPACE_EDGES, '');
  // Python's `len` and slicing count code points; JS counts UTF-16 units, so a
  // title of astral characters would be cut mid-surrogate at a length the
  // server never even trims. Count what Python counts.
  const chars = Array.from(cleaned);
  if (chars.length > max) {
    cleaned = chars.slice(0, max).join('').replace(PY_SPACE_TRAIL, '');
  }
  return cleaned;
}

/**
 * Mirror of `_resolve_journal_path` in journal.py, sanitising the title first
 * exactly as the entry-creation service does before calling it.
 */
export function resolveJournalPath(
  isoDate: string,
  title: string | null,
  defaults: JournalDefaults
): string {
  const { y, m, d, dow, monthName } = parseDate(isoDate);
  const context: Record<string, string> = {
    journal_folder: '',
    year: String(y),
    month: two(m),
    month_name: monthName,
    day: two(d),
    date: isoDate,
    day_of_week: DAY_NAMES[dow] ?? '',
    title: title ? sanitizeTitle(title, defaults.title_max_length) : '',
  };
  const raw = defaults.path_pattern
    .replace(/\{(\w+)\}/g, (_match, key: string) => {
      const value = context[key];
      // Python's `str.format` raises KeyError on an unknown field; a silent ''
      // here would quietly write the entry to a different path than the server.
      if (value === undefined) {
        throw new Error(`journal path pattern uses unknown key {${key}}`);
      }
      return value;
    })
    .replace(PY_SPACE_EDGES, '');
  // The server finishes with `PurePosixPath(...).as_posix()`, which is a no-op
  // after these three: the pattern always ends in `.md` (so there is no
  // trailing slash or `.` segment to normalise) and `journal_folder` is ''.
  return raw.replace(PY_SPACE_BEFORE_MD, '.md').replace(/\/+/g, '/').replace(/^\/+/, '');
}

/**
 * Mirror of `render_journal_template` in journal.py, with the same sanitised
 * title and the same `"%B %d, %Y"` fallback when there is none.
 */
export function renderJournalTemplate(
  isoDate: string,
  title: string | null,
  defaults: JournalDefaults
): string {
  const { y, m, d, dow, monthName } = parseDate(isoDate);
  const cleaned = title ? sanitizeTitle(title, defaults.title_max_length) : '';
  // Python's `title or strftime(...)` falls back on an empty string too, so
  // `||` is the faithful operator here, not `??`.
  const renderedTitle = cleaned || `${monthName} ${two(d)}, ${y}`;
  const replacements: Record<string, string> = {
    '{{date}}': isoDate,
    '{{year}}': String(y),
    '{{month}}': two(m),
    '{{day}}': two(d),
    '{{title}}': renderedTitle,
    '{{day_of_week}}': DAY_NAMES[dow] ?? '',
  };
  let text = defaults.template;
  for (const [placeholder, value] of Object.entries(replacements)) {
    text = text.split(placeholder).join(value);
  }
  return text;
}
